import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Popover } from 'antd'
import { ArticleColumn } from '../../Constants/TableConfiguration'
import { CloseOutlined } from '@ant-design/icons'
import { getImageFromUrl } from '../../Services/PIM/ImageService'
import { retrieveSelectedArticleKey } from '../../Helpers/ArticlesHelper'
import { useAppSelector } from '../../hooks'
import { missingImageGen, notFoundImageGen } from '../../Constants/Configuration'

function StickyArticleSelection() {
  const articles = useAppSelector(state => state.present.article.value)
  const rowKeysSelected = useAppSelector(state => state.present.article.rowKeysSelected)

  const articleKey: any = retrieveSelectedArticleKey(rowKeysSelected)
  const articlesResults = useMemo(() => {
    return articles ? articles.results : []
  }, [articles])
  const articleInfo = useMemo(() => {
    return articlesResults.filter(result => result.key === articleKey)
  }, [articlesResults, articleKey])
  const articleColumnsKey = useMemo(() => {
    return articleKey && articleInfo.length !== 0 ? Object.keys(articleInfo[0]) : []
  }, [articleInfo, articleKey])

  const [isVisible, setIsVisible] = useState(false)
  const [isClose, setCloseArticle] = useState(false)
  const [heightToHideFrom, setHeightToHideFrom] = useState(0)

  const getSelectedRowOffset = useCallback(element => {
    const rect = element?.getBoundingClientRect()
    const scrollTop = window.scrollY || document.documentElement.scrollTop

    return rect && rect.bottom + scrollTop
  }, [])

  const listenToScroll = useCallback(() => {
    const winScroll = document.body.scrollTop || document.documentElement.scrollTop
    const element = document.getElementsByClassName('ant-table-row-selected')

    if (element && element.length !== 0) {
      if (winScroll > heightToHideFrom) {
        setIsVisible(true)
      } else {
        isVisible && setIsVisible(false)
      }
    }
  }, [isVisible, heightToHideFrom])

  useEffect(() => {
    window.addEventListener('scroll', listenToScroll, { passive: true })
    return () => window.removeEventListener('scroll', listenToScroll)
  }, [listenToScroll])

  useEffect(() => {
    setCloseArticle(false)
    const element = document.getElementsByClassName('ant-table-row-selected')
    setHeightToHideFrom(getSelectedRowOffset(element[0]))
  }, [articleKey, getSelectedRowOffset])

  const articleImage = useMemo(() => {
    if (articleInfo.length !== 0 && articleInfo[0].images.length !== 0) {
      const defaults = articleInfo[0].images.filter(x => x.default)
      if (defaults.length) {
        return defaults[0].url
      } else {
        return articleInfo[0].images[0].url
      }
    }
    return ''
  }, [articleInfo])

  return articleKey && isVisible && !isClose ? (
    <div className={'sticky-selection' + (isVisible ? ' anim' : '')}>
      <div className='close'>
        <CloseOutlined
          className='filter-drawer-close-icon'
          onClick={() => {
            setCloseArticle(true)
          }}
        />
      </div>
      <div className='image-box'>
        {articleColumnsKey.map((columnKey, index) => {
          if (columnKey === ArticleColumn.ArticleCode) {
            return articleInfo[0]['images'].length !== 0 ? (
              <div key={'img-' + index}>
                <img
                  src={getImageFromUrl(articleImage)}
                  alt=''
                  className='w-100'
                  onError={e => {
                    const target = e.target as HTMLImageElement
                    target.onerror = null
                    target.src = notFoundImageGen
                  }}
                />
              </div>
            ) : (
              <div key={'img-' + index}>
                <img src={missingImageGen} className='w-100' alt='' />
              </div>
            )
          }

          return null
        })}
      </div>
      <div className='info-box'>
        {articleColumnsKey.map((columnKey, index) => {
          if (columnKey === ArticleColumn.ArticleCode) {
            const text = articleInfo[0][columnKey]
            return text && text.length >= 30 ? (
              <Popover content={text} placement='bottom' trigger='hover'>
                <div key={columnKey + '-' + index} className='item article-code-label truncate-text'>
                  {text.substr(0, 30) + '...'}
                </div>
              </Popover>
            ) : (
              <div key={columnKey + '-' + index} className='item article-code-label truncate-text'>
                {text}
              </div>
            )
          }
          if (columnKey === ArticleColumn.Brand) {
            return (
              <div key={columnKey + '-' + index} className='item brand-label truncate-text'>
                {articleInfo[0][columnKey]}
              </div>
            )
          }
          if (columnKey === ArticleColumn.SubCategory) {
            return (
              <div key={columnKey + '-' + index} className='item truncate-text'>
                {articleInfo[0][columnKey]}
              </div>
            )
          }

          return null
        })}
      </div>
    </div>
  ) : null
}

export default StickyArticleSelection
